import React, { useState, useEffect } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import apiService from '../../services/api'; // 🟢 引入 API
import UserAvatar from '../common/UserAvatar';
import TutorialOverlay from '../common/TutorialOverlay';
import DailyTasksCard from '../dashboard/DailyTasksCard';
import { useProgress, useSystemBadges } from '../../hooks/queries';

const DashboardPage = () => {
const { user, reloadUser } = useAuth();

  // 🟢 [P2] React Query：进度统计与系统徽章改为缓存化读取（本页仅学生可见）
  const { data: progressRes } = useProgress({ enabled: user?.role === 'student' });
  const { data: systemBadges = [] } = useSystemBadges({ enabled: !!user });
  const stats = progressRes?.progress;

  // 🟢 Tutorial State
  const [showTutorial, setShowTutorial] = useState(false);
  
  useEffect(() => {
    if (user?.role === 'student' && user?.tutorialCompleted !== true) {
      setShowTutorial(true);
    }
  }, [user]);

  const handleTutorialComplete = async () => {
    setShowTutorial(false);
    try {
      await apiService.updateProfile({ tutorialCompleted: true });
    } catch(e) { console.error('Failed to save tutorial status:', e); }
  };

  const tutorialSteps = [
    { targetId: 'tutorial-welcome', content: 'Welcome to Vocab Run! This is your dashboard.', position: 'bottom' },
    { targetId: 'tutorial-start-learning', content: 'Start here! Work through the lessons in order — you must finish the earlier lessons before the Final Quiz unlocks. Completing lessons also unlocks the games.', position: 'bottom' },
    { targetId: 'tutorial-play-games', content: 'Once you learn some words, play games to reinforce your memory!', position: 'bottom' },
    { targetId: 'tutorial-stats', content: 'Track your progress and keep up your daily streak!', position: 'top' },
    { targetId: 'tutorial-badges', content: 'Earn badges as you learn and hit milestones — collect them all here!', position: 'top' },
    { targetId: 'tutorial-visit-shop', content: 'Earn points and spend them in the Shop to customize your avatar.', position: 'left' }
  ];

  // 🟢 学生进入 Dashboard 时刷新积分/每日任务（每个用户仅跑一次，避免与 React Query 重复）
  useEffect(() => {
    if (user?.role === 'student') reloadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const quickActions = [
    {
      title: 'Start Learning',
      description: 'Continue your vocabulary journey',
      icon: '📚',
      link: '/courses',
      color: 'from-blue-300 to-purple-400'
    },
    {
      title: 'Play Games',
      description: 'Fun vocabulary games',
      icon: '🎮',
      link: '/games',
      color: 'from-green-300 to-blue-300'
    },
    {
      title: 'View Progress',
      description: 'Check your achievements',
      icon: '📊',
      link: '/progress',
      color: 'from-orange-300 to-red-300'
    },
    {
      title: 'Visit Shop',
      description: 'Spend your points',
      icon: '🛒',
      link: '/shop',
      color: 'from-purple-300 to-pink-300'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <motion.div
          id="tutorial-welcome"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8 text-gray-800"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">

            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-2">
                <h1 className="text-4xl font-bold">Welcome back, {user?.name}!</h1>
                <UserAvatar
                  avatarId={user?.avatarId}
                  customAvatarUrl={user?.customAvatarUrl}
                  inventory={user?.inventory}
                  size="sm"
                  className="border-2 border-white shadow-md"
                />
              </div>
              <p className="text-xl text-gray-700">Ready to continue your vocabulary adventure?</p>
            </div>
            
            {user?.role === 'student' && (
              <div className="text-right bg-white/40 p-5 rounded-2xl">
                <div className="text-4xl font-black text-yellow-600"><AppIcon name="coins" className="mr-2" />{user.points || 0}</div>
                <div className="text-sm text-gray-700 font-bold mt-1">Total Points</div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link
                id={`tutorial-${action.title.toLowerCase().replace(' ', '-')}`}
                to={action.link}
                className={`block bg-gradient-to-r ${action.color} rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300`}
              >
                <div className="text-4xl mb-4">{action.icon}</div>
                <h3 className="text-xl font-bold mb-2">{action.title}</h3>
                <p className="text-sm opacity-90">{action.description}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* 🟢 Daily Tasks */}
        {user?.role === 'student' && (
          <div className="mb-6">
            <DailyTasksCard dailyTasks={user?.dailyTasks} />
          </div>
        )}

        {/* Recent Activity & Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Badges */}
          {user?.role === 'student' && (
            <motion.div
              id="tutorial-badges"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-gray-800"
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                <AppIcon name="medal" className="mr-2 text-yellow-500" />
                Recent Badges
              </h2>
              {user.badges && user.badges.length > 0 ? (
                <div className="space-y-3">
                  {[...user.badges].reverse().slice(0, 3).map((badgeId, index) => {
                    // 🟢 1. 在系统徽章库中查找详细信息
                    // 您的截图确认数据库中徽章有 'id' 字段，例如 "badge_the_flash"
                    const foundBadge = systemBadges.find(sb => sb.id === badgeId);

                    // 🟢 2. 智能兜底逻辑
                    // 如果找到了，用数据库里的 Name 和 Icon
                    // 如果没找到 (缓存延迟)，把 badge_the_flash 格式化为 "The Flash"
                    const displayName = foundBadge 
                      ? foundBadge.name 
                      : badgeId.replace(/^badge_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    
                    const displayIcon = foundBadge ? foundBadge.icon : '🏅';

                    return (
                      <div key={index} className="flex items-center space-x-3 bg-white bg-opacity-40 rounded-lg p-3 shadow-sm border border-white/50">
                        <div className="text-2xl filter drop-shadow-sm">{displayIcon}</div>
                        <div>
                          <div className="font-bold text-gray-800">{displayName}</div>
                          <div className="text-xs text-gray-600 font-medium uppercase tracking-wide">Earned</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-600 bg-white/30 rounded-lg p-4">
                  Start learning to earn your first badge!
                </p>
              )}
            </motion.div>
          )}

          {/* Quick Stats */}
          <motion.div
            id="tutorial-stats"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-gray-800"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              <AppIcon name="chart-bar" className="mr-2 text-yellow-500" />
              Your Stats
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Words Learned</span>
                <span className="font-bold text-xl">
                  {/* 🟢 修改：使用 stats 中的真实数据 */}
                  {user?.role === 'student' ? (stats?.totalWordsLearned || 0) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Lessons Completed</span>
                <span className="font-bold text-xl">
                   {/* 🟢 修改：使用 stats 中的真实数据 */}
                  {user?.role === 'student' ? (stats?.totalLessonsCompleted || 0) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Games Played</span>
                <span className="font-bold text-xl">
                   {/* 🟢 修改：使用 stats 中的真实数据 */}
                  {user?.role === 'student' ? (stats?.totalGamesPlayed || 0) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Current Streak</span>
                <span className="font-bold text-xl text-yellow-300">
                   {/* 🟢 修改：使用 user 对象中的 streak (由 auth.js 维护) */}
                  {user?.role === 'student' ? `${user?.streak || 0} days` : 'N/A'}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Role-specific content */}
        {user?.role === 'admin' && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-gray-800"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              <AppIcon name="cog" className="mr-2 text-yellow-500" />
              Admin Panel
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/admin"
                className="bg-white bg-opacity-20 rounded-lg p-4 hover:bg-opacity-30 transition-all"
              >
                <AppIcon name="users" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">Manage Users</div>
                <div className="text-sm text-gray-600">Approve registrations</div>
              </Link>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <AppIcon name="book" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">Vocabulary</div>
                <div className="text-sm text-gray-600">Manage word lists</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <AppIcon name="chart-line" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">Analytics</div>
                <div className="text-sm text-gray-600">Platform statistics</div>
              </div>
            </div>
          </motion.div>
        )}

        {user?.role === 'teacher' && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-gray-800"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              <AppIcon name="chalkboard-teacher" className="mr-2 text-yellow-500" />
              Teacher Dashboard
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/teacher"
                className="bg-white bg-opacity-20 rounded-lg p-4 hover:bg-opacity-30 transition-all"
              >
                <AppIcon name="users" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">My Classes</div>
                {/* 🟢 修复：使用 classIds 数组长度 */}
                <div className="text-sm text-gray-600">
                  {user.classIds && user.classIds.length > 0 ? `${user.classIds.length} Classes Managed` : 'No classes assigned'}
                </div>
              </Link>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <AppIcon name="tasks" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">Assignments</div>
                <div className="text-sm text-gray-600">Create vocabulary lists</div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-4">
                <AppIcon name="chart-bar" className="text-2xl mb-2 text-yellow-500" />
                <div className="font-semibold">Student Progress</div>
                <div className="text-sm text-gray-600">Track performance</div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {showTutorial && (
        <TutorialOverlay steps={tutorialSteps} onComplete={handleTutorialComplete} />
      )}
    </div>
  );
};

export default DashboardPage;
